import { Store, MapPin, Rocket, Check } from 'lucide-react';
import type { OnboardingFormData } from '../hooks/useOnboardingState';

interface ConfirmationStepProps {
  data: OnboardingFormData;
  isSubmitting: boolean;
}

/**
 * Step 3: Confirmation and summary
 *
 * Displays:
 * - Business information summary
 * - Selected markets summary
 * - Terms acceptance (implicit)
 * - Create button
 */
export function ConfirmationStep({ data, isSubmitting }: ConfirmationStepProps) {
  const { tenantInfo, marketSelection } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
          <Check className="w-5 h-5 text-orange-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Récapitulatif</h2>
          <p className="text-sm text-gray-500">Vérifiez vos informations avant de créer votre compte</p>
        </div>
      </div>

      {/* Business info summary */}
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-500 font-medium text-sm uppercase tracking-wide">
          <Store className="h-4 w-4" />
          Mon commerce
        </div>

        <div className="grid gap-2">
          <div className="flex justify-between">
            <span className="text-gray-600">Nom</span>
            <span className="font-medium text-gray-900">{tenantInfo.name || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">URL</span>
            <span className="font-medium text-gray-900">
              order-paulownia.web.app/{tenantInfo.slug || '—'}
            </span>
          </div>
          {tenantInfo.description && (
            <div className="flex justify-between">
              <span className="text-gray-600">Description</span>
              <span className="font-medium text-gray-900 text-right max-w-xs truncate">
                {tenantInfo.description}
              </span>
            </div>
          )}
          {tenantInfo.phoneNumber && (
            <div className="flex justify-between">
              <span className="text-gray-600">Téléphone</span>
              <span className="font-medium text-gray-900">{tenantInfo.phoneNumber}</span>
            </div>
          )}
          {tenantInfo.openingHours && (
            <div className="flex justify-between">
              <span className="text-gray-600">Horaires</span>
              <span className="font-medium text-gray-900">{tenantInfo.openingHours}</span>
            </div>
          )}
        </div>
      </div>

      {/* Markets summary */}
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-gray-500 font-medium text-sm uppercase tracking-wide">
          <MapPin className="h-4 w-4" />
          Mes marchés ({marketSelection.selectedMarkets.length})
        </div>

        {marketSelection.selectedMarkets.length > 0 ? (
          <ul className="space-y-2">
            {marketSelection.selectedMarkets.map((selected) => (
              <li key={selected.marketId} className="flex items-center gap-2 text-gray-900">
                <div className="w-2 h-2 bg-orange-500 rounded-full" />
                <span className="font-medium">{selected.market.city}</span>
                <span className="text-gray-500">— {getDayName(selected.market.dayOfWeek)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500 italic text-sm">
            (La sélection des marchés sera implémentée en Phase 8)
          </p>
        )}
      </div>

      {/* Ready message */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
        <Rocket className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-green-800">
            Tout est prêt !
          </p>
          <p className="text-sm text-green-700 mt-1">
            Cliquez sur "Créer mon commerce" pour finaliser votre inscription.
          </p>
        </div>
      </div>

      {/* Terms notice */}
      <p className="text-xs text-gray-500 text-center">
        En créant votre commerce, vous acceptez les{' '}
        <a href="#" className="text-orange-600 hover:underline">
          conditions générales d'utilisation
        </a>
        .
      </p>

      {/* Loading state overlay */}
      {isSubmitting && (
        <div className="fixed inset-0 bg-white/80 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-gray-600">Création de votre commerce...</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Get French day name from ISO day number (1-7)
 */
function getDayName(dayOfWeek: number): string {
  const days = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
  return days[dayOfWeek] || '';
}
